import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";

const STATUS_BADGE = (approved, deed) => {
    if (!deed) return { txt: "Deed unsigned", cls: "text-silver-400 border-white/15" };
    if (!approved) return { txt: "Awaiting admin", cls: "text-platinum border-platinum/40" };
    return { txt: "Listed", cls: "text-emerald-400 border-emerald-400/40" };
};

export default function MyFilms() {
    const [films, setFilms] = useState([]);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/my/films");
                setFilms(data);
            } catch (e) {
                setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-16">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-2">
                            Creator studio
                        </div>
                        <h1 className="font-display text-5xl">My films</h1>
                    </div>
                    <Link
                        to="/creator"
                        className="bg-platinum hover:bg-platinum-200 text-ink-300 px-5 py-2.5 rounded-sm uppercase tracking-widest text-xs"
                        data-testid="upload-new-btn"
                    >
                        Upload new
                    </Link>
                </div>

                {err && <div className="text-red-400 text-sm mb-4">{err}</div>}

                {loading ? (
                    <div className="text-silver-500 animate-pulse">Loading…</div>
                ) : films.length === 0 ? (
                    <div className="glass p-10 text-center rounded-sm">
                        No films yet. <Link to="/creator" className="text-platinum">Upload your first</Link>.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {films.map((f, i) => {
                            const badge = STATUS_BADGE(f.admin_approved, f.deed_signed);
                            return (
                                <motion.div
                                    key={f.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="glass p-4 rounded-sm"
                                >
                                    <div className="aspect-[2/3] rounded-sm overflow-hidden mb-3 bg-ink-200">
                                        {f.poster_url && (
                                            <img src={f.poster_url} alt={f.title} className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    <div className="font-display text-xl mb-1">{f.title}</div>
                                    <div className="text-xs text-silver-500 mb-3">
                                        {f.country} · {f.language}
                                    </div>
                                    <div className={`inline-block text-[10px] uppercase tracking-widest border px-2 py-1 rounded-sm ${badge.cls}`}>
                                        {badge.txt}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
